/**
 * Analytics Main Page Component
 *
 * Main orchestrator for the analytics page with grid and analytics views
 */

import React from 'react'
import { BarChart3 } from 'lucide-react'
import { useSyncedStore } from '@syncedstore/react'
import { v4 as uuidv4 } from 'uuid'
import {
  todoStore,
  addWorkerAssignmentLog,
  softDeleteWorkerAssignmentLog,
  getDoc,
  getClientId,
} from 'src/store/todo'
import { useParcelStore } from 'src/store/parcel'
import type { TaskType, Zone } from 'src/store/todo/types'
import type {
  ViewMode,
  RecordModalState,
  SummaryModalState,
  DoneModalState,
} from './types'
import { getWorkerAllHarvestsSummary } from './utils/filtering'
import { checkForDuplicates } from 'src/utils/duplicates'
import PageHeader from 'src/components/shared/page-header'
import Container from 'src/components/shared/container'
import Loader from 'src/components/shared/loader'
import Fallback from 'src/components/shared/fallback'
import RecordValueModal from 'src/components/shared/record-value-modal'
import WorkerValueSummaryModal from 'src/components/shared/worker-value-summary-modal'
import DoneLocationModal from 'src/components/shared/done-location-modal'
import TeamSelector from './components/team-selector'
import GridView from './components/grid-view'
import AnalyticsView from './components/analytics-view'

const STORAGE_KEY = 'activities_selected_team'

/**
 * AnalyticsMain component - Main analytics page with grid/analytics toggle
 */
const AnalyticsMain: React.FC = () => {
  const [selectedTeamId, setSelectedTeamId] = React.useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY)
  })
  const [selectedTasks, setSelectedTasks] = React.useState<string[]>([])
  const [recordModalState, setRecordModalState] = React.useState<RecordModalState>(null)
  const [summaryModalState, setSummaryModalState] = React.useState<SummaryModalState>(null)
  const [doneModalState, setDoneModalState] = React.useState<DoneModalState>(null)
  const [deleteConfirmLogId, setDeleteConfirmLogId] = React.useState<string | null>(null)
  const [viewMode, setViewMode] = React.useState<ViewMode>('grid')
  const { parcel: parcelId } = useParcelStore()

  const state = useSyncedStore(todoStore)
  const teams = state.teams || []

  // Check for duplicates and warn
  React.useEffect(() => {
    checkForDuplicates(teams, 'Teams')
  }, [teams.length])

  // Get data from Yjs store
  const zones = (state.zones?.filter((z: any) => z.parcelId === parcelId && z.isActive) ||
    []) as Zone[]
  const tasks = (state.taskTypes || []) as TaskType[]

  // Initialize all tasks as selected on first load
  const tasksInitialized = React.useRef(false)
  React.useEffect(() => {
    if (!tasksInitialized.current && tasks.length > 0) {
      setSelectedTasks(tasks.map((t) => t.id))
      tasksInitialized.current = true
    }
  }, [tasks])

  /**
   * Handle team selection
   */
  const handleTeamSelect = (teamId: string) => {
    setSelectedTeamId(teamId)
    localStorage.setItem(STORAGE_KEY, teamId)
  }

  /**
   * Toggle task filter
   */
  const toggleTask = (taskId: string) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId) ? prev.filter((t) => t !== taskId) : [...prev, taskId]
    )
  }

  /**
   * Handle saving a recorded value
   */
  const handleSaveRecordedValue = (value: number) => {
    if (recordModalState && selectedTeamId) {
      try {
        addWorkerAssignmentLog(
          selectedTeamId,
          recordModalState.workerId,
          recordModalState.taskTypeId,
          recordModalState.zoneId,
          value,
          recordModalState.locationId
        )
        // Auto-mark as completed for value-type tasks
        const assignment = state.workerAssignments.find(
          (a) =>
            a.workerId === recordModalState.workerId &&
            a.taskTypeId === recordModalState.taskTypeId &&
            a.zoneId === recordModalState.zoneId &&
            a.locationId === recordModalState.locationId &&
            !a.softDeleted
        )
        if (assignment && !assignment.completed) {
          assignment.completed = true
        }
        setRecordModalState(null)
      } catch (error) {
        console.error('Error saving recorded value:', error)
      }
    }
  }

  /**
   * Handle opening worker summary modal
   * Now accepts array of harvest task IDs to show complete tally
   */
  const handleOpenSummaryModal = (
    workerId: string,
    workerName: string,
    harvestTaskIds: string[]
  ) => {
    setSummaryModalState({
      isOpen: true,
      workerId,
      workerName,
      taskTypeId: '', // Not used anymore - we show all harvest tasks
      taskName: 'All Harvests',
      harvestTaskIds,
    })
  }

  /**
   * Handle confirming zone completion with selected locations
   */
  const handleConfirmZoneDone = (selectedLocationIds: string[], completionsToDelete: string[]) => {
    if (doneModalState && selectedTeamId) {
      try {
        // Get existing completions
        const existingCompletions = state.workerAssignmentLogs.filter(
          (log: any) =>
            log.workerId === doneModalState.workerId &&
            log.taskTypeId === doneModalState.taskTypeId &&
            log.zoneId === doneModalState.zoneId &&
            log.locationId &&
            !log.softDeleted
        )

        const existingLocationIds = existingCompletions.map((c: any) => c.locationId)

        // Soft delete completions that were unselected
        completionsToDelete.forEach((logId) => {
          softDeleteWorkerAssignmentLog(logId)
        })

        // Create new logs for newly selected locations
        const batchTimestamp = Date.now()
        selectedLocationIds.forEach((locationId) => {
          if (!existingLocationIds.includes(locationId)) {
            const doc = getDoc()
            if (doc) {
              const newLog = {
                id: uuidv4(),
                workerId: doneModalState.workerId,
                teamId: selectedTeamId,
                taskTypeId: doneModalState.taskTypeId,
                zoneId: doneModalState.zoneId,
                locationId,
                value: 1,
                timestamp: batchTimestamp,
                clientId: getClientId()?.toString() || null,
              }
              doc.transact(() => {
                state.workerAssignmentLogs.push(newLog)
              })
            }
          }
        })

        setDoneModalState(null)
      } catch (error) {
        console.error('Error confirming zone completion:', error)
      }
    }
  }

  /**
   * Handle confirming entire zone completion (no specific locations)
   */
  const handleConfirmZoneAll = () => {
    if (doneModalState && selectedTeamId) {
      try {
        const doc = getDoc()
        if (doc) {
          const newLog = {
            id: uuidv4(),
            workerId: doneModalState.workerId,
            teamId: selectedTeamId,
            taskTypeId: doneModalState.taskTypeId,
            zoneId: doneModalState.zoneId,
            value: 1,
            timestamp: Date.now(),
            clientId: getClientId()?.toString() || null,
          }
          doc.transact(() => {
            state.workerAssignmentLogs.push(newLog)
          })
        }
        setDoneModalState(null)
      } catch (error) {
        console.error('Error confirming zone completion:', error)
      }
    }
  }

  /**
   * Handle canceling zone completion
   */
  const handleCancelZoneCompletion = (completionId: string) => {
    try {
      softDeleteWorkerAssignmentLog(completionId)
      setDoneModalState(null)
    } catch (error) {
      console.error('Error canceling zone completion:', error)
    }
  }

  const selectedTeam = teams.find((t) => t.id === selectedTeamId)

  // Team selection view
  if (!selectedTeamId) {
    return (
      <div className="min-h-screen w-full flex flex-col bg-gray-50">
        <PageHeader title="Activities" />

        <div className="flex-1 overflow-y-auto">
          <Container>
            <div className="flex flex-col gap-6 py-6">
              <div>
                <h2 className="text-lg font-semibold mb-4">Select a Team</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {teams.length > 0 ? (
                    teams.map((team) => (
                      <button
                        key={team.id}
                        onClick={() => handleTeamSelect(team.id)}
                        className="p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-green-50 transition-colors text-left"
                      >
                        <h3 className="font-semibold text-gray-900">{team.teamName}</h3>
                        <p className="text-sm text-gray-600 mt-1">{team.teamDescription}</p>
                      </button>
                    ))
                  ) : (
                    <Fallback title="No teams found" />
                  )}
                </div>
              </div>
            </div>
          </Container>
        </div>
      </div>
    )
  }

  // Loading state
  if (zones.length === 0 || tasks.length === 0) return <Loader />

  // Main analytics page
  return (
    <div className="min-h-screen w-full flex flex-col bg-gray-50">
      <PageHeader
        title={selectedTeam?.teamName || 'Activities'}
        rightContent={
          <div className="flex items-center gap-3">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'analytics' : 'grid')}
              className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                viewMode === 'analytics'
                  ? 'bg-green-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
              title="Toggle Analytics View"
            >
              <BarChart3 size={16} />
            </button>
            <TeamSelector
              selectedTeamId={selectedTeamId}
              teams={teams}
              onTeamSelect={handleTeamSelect}
            />
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto">
        <Container>
          {viewMode === 'analytics' ? (
            <AnalyticsView
              selectedTeamId={selectedTeamId}
              onShowWorkerSummary={handleOpenSummaryModal}
            />
          ) : (
            <GridView
              selectedTeamId={selectedTeamId}
              selectedTasks={selectedTasks}
              onToggleTask={toggleTask}
              parcelId={parcelId}
            />
          )}
        </Container>
      </div>

      {/* Record Value Modal */}
      {recordModalState && (
        <RecordValueModal
          isOpen={recordModalState.isOpen}
          title="# of Boxes"
          onSave={handleSaveRecordedValue}
          onCancel={() => setRecordModalState(null)}
        />
      )}

      {/* Summary Modal */}
      {summaryModalState && (
        <WorkerValueSummaryModal
          isOpen={summaryModalState.isOpen}
          workerName={summaryModalState.workerName}
          taskName={summaryModalState.taskName}
          summaryData={getWorkerAllHarvestsSummary(
            state.workerAssignmentLogs,
            zones,
            state.locations || [],
            summaryModalState.workerId,
            summaryModalState.harvestTaskIds || []
          )}
          onClose={() => setSummaryModalState(null)}
        />
      )}

      {/* Done Location Modal */}
      {doneModalState && (() => {
        const zoneCompletion = state.workerAssignmentLogs.find(
          (log: any) =>
            log.workerId === doneModalState.workerId &&
            log.taskTypeId === doneModalState.taskTypeId &&
            log.zoneId === doneModalState.zoneId &&
            !log.locationId &&
            !log.softDeleted
        )

        return (
          <DoneLocationModal
            isOpen={doneModalState.isOpen}
            zone={{
              id: doneModalState.zoneId,
              zone_moniker: doneModalState.zoneName,
              locations: state.locations
                .filter((l: any) => l.zoneId === doneModalState.zoneId && l.isActive)
                .map((l: any) => ({
                  id: l.id,
                  location_moniker: l.locationMoniker,
                })),
            }}
            workerName={doneModalState.workerName}
            taskName={doneModalState.taskName}
            existingCompletions={state.workerAssignmentLogs
              .filter(
                (log: any) =>
                  log.workerId === doneModalState.workerId &&
                  log.taskTypeId === doneModalState.taskTypeId &&
                  log.zoneId === doneModalState.zoneId &&
                  log.locationId &&
                  !log.softDeleted
              )
              .map((log: any) => ({
                id: log.id,
                locationId: log.locationId,
              }))}
            existingZoneCompletion={zoneCompletion ? { id: zoneCompletion.id } : undefined}
            onConfirm={handleConfirmZoneDone}
            onConfirmZone={handleConfirmZoneAll}
            onCancelZone={handleCancelZoneCompletion}
            onCancel={() => setDoneModalState(null)}
          />
        )
      })()}

      {/* Delete Confirmation Modal */}
      {deleteConfirmLogId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xs mx-4">
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Record?</h3>
              <p className="text-sm text-gray-600 mb-6">
                This record will be marked as deleted but kept for audit purposes.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setDeleteConfirmLogId(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    softDeleteWorkerAssignmentLog(deleteConfirmLogId)
                    setDeleteConfirmLogId(null)
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AnalyticsMain
